import { useEffect } from 'react';

import { Logger } from '../../../logger/Logger';
import { useAutoplayHiddenFlag } from '../../../store/compositeSelectors/useAutoplayHiddenFlag';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useAutoplaySlice } from '../../../store/slices/autoplay/selectors';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { useBetSlice } from '../../../store/slices/bets/selectors';
import { useBonusPromotionsSlice } from '../../../store/slices/bonusPromotions/selectors';
import { useChipSlice } from '../../../store/slices/chips/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { toastActions } from '../../../store/slices/toast/slice';
import { placeAutoplayBet } from '../gameControls/autoplayControl/utils/placeAutoplayBet';

/**
 * This hook listens for a pending autoplay bet and dispatches it.
 * This way we can avoid having fetch calls in the middleware.
 */
export const useAutoplayBetDispatcher = (): void => {
  const dispatch = useCoreDispatch();
  const hideAutoplay = useAutoplayHiddenFlag();
  const hasPendingAutoplayBet = useAutoplaySlice.hasPendingAutoplayBet();
  const savedBet = useAutoplaySlice.getSavedBet();
  const disabledBetSpots = useBetSlice.getDisabledBetSpots();
  const balance = useGameStateSlice.balance();
  const digitsNumber = useGameStateSlice.getCurrencyInfo()?.digitsNumber;
  const freeChipValue = useBonusPromotionsSlice.getFreeChipValue();
  const freeChipBalance = useBonusPromotionsSlice.getFreeChipBalance();
  const remainingFreeChips = useChipSlice.getFreeChipsRemaining() ?? 0;
  const disableAutoPlayForFreeChip = useAutoplaySlice.isDisableAutoPlayForFreeChip(remainingFreeChips);

  useEffect(() => {
    if (!hideAutoplay && hasPendingAutoplayBet && savedBet) {
      if (disableAutoPlayForFreeChip) {
        // stop autoplay if free chips are expired or completed or have low balance
        dispatch(toastActions.createToast('AUTOPLAY_SUPPORT_CURR_BAL'));
        Logger.logEvent('autoplay_stop', {
          additional: 'FreeChipLowBalance',
        });
        dispatch(autoplayActions.stop());
      }

      dispatch(autoplayActions.setPendingAutoplayBet(false));
      placeAutoplayBet(
        true,
        savedBet,
        disabledBetSpots || [],
        freeChipValue,
        freeChipBalance,
        dispatch,
        balance,
        digitsNumber
      );
    }
  }, [
    balance,
    digitsNumber,
    disabledBetSpots,
    dispatch,
    hasPendingAutoplayBet,
    savedBet,
    hideAutoplay,
    freeChipValue,
    freeChipBalance,
    disableAutoPlayForFreeChip,
  ]);
};
